/* eslint-disable react/no-unknown-property */

import { ComponentResource } from '@/api/serverApi'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedComponent } from '@/stores/active-design'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'
import { useMemo } from 'react'
import { Vector3 } from 'three'
import { RootState } from '@/stores'
import useComponentParent from '@/hooks/useComponentParent'
import DistanceArrow3D from '../DistanceArrow3D'
import CastInChannelGLTF from './CastInChannelModel'
import { Html } from '@react-three/drei'
import { LoadingSpinner } from '@cfs-fixings/shared'
import EdgeDistanceValues from '@/consts/EdgeDistanceValues'

const distanceArrowVerticalOffsetFromSlab = 5
const distanceArrowHorizontalOffsetFromSlab = 60
const referenceOffsetFromArrow = 15
const loadingSpinnerVerticalOffset = 75
const loadingSpinnerHorizontalOffset = -32

type PropTypes = {
  component: ComponentResource
}

type DistancePointType = {
  label: string,
  distanceArrowStartPoint: [number, number, number],
  distanceArrowEndPoint: [number, number, number],
  startReferenceLinePoints: [[number, number, number], [number, number, number]],
  endReferenceLinePoints: [[number, number, number], [number, number, number]],
}

const CastInChannelContainer = ({ component }: PropTypes) => {
  const dispatch = useDispatch()

  const productVariants = useSelector((state: RootState) => state.products.productVariants)

  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  const { parent: slab } = useComponentParent({ component: component })

  const productVariant = productVariants?.find(p => p.id === component.product_variant_id)

  const channelOffset = useMemo(() => {
    const details = slab?.concrete_slab

    if (!details) {
      return new Vector3(0, 0, 0)
    }

    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const channelLength = component?.cast_in_anchor_channel?.custom_length ?? component?.cast_in_anchor_channel?.length ?? 0

    const halfThickness = details.thickness / 2
    const halfChannelDepth = (productVariant?.cast_in_anchor_channel?.depth ?? 0) / 2
    const halfChannelWidth = (productVariant?.cast_in_anchor_channel?.width ?? 0) / 2
    const halfSlabHeight = (yPosEdgeDistance + yNegEdgeDistance + (productVariant?.cast_in_anchor_channel?.width ?? 0)) / 2
    const halfSlabWidth = (xPosEdgeDistance + xNegEdgeDistance + channelLength) / 2
    const halfChannelLength = channelLength / 2

    // The additional 0.1 is needed to reduce overlapping geometry. This appears to cause
    // some issues with the CSG operation that caused crashes
    const yOffset = (halfThickness - halfChannelDepth) + 0.1

    const xOffset = (halfSlabWidth - halfChannelLength - xPosEdgeDistance)
    const zOffset = (halfSlabHeight - halfChannelWidth - yNegEdgeDistance)

    return new Vector3(xOffset, yOffset, zOffset)
  }, [slab, productVariant, component])

  const arrowPlacements = useMemo(() => {
    let positions: DistancePointType[] = []

    const details = slab?.concrete_slab

    if (!details) {
      return []
    }

    const channelLength = component?.cast_in_anchor_channel?.custom_length ?? component?.cast_in_anchor_channel?.length ?? 0

    const halfChannelDepth = (productVariant?.cast_in_anchor_channel?.depth ?? 0) / 2
    const halfChannelWidth = (productVariant?.cast_in_anchor_channel?.width ?? 0) / 2
    const halfChannelLength = channelLength / 2

    const verticalOffset = (halfChannelDepth) + distanceArrowVerticalOffsetFromSlab

    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const xHorizontalOffset = (yPosEdgeDistance + halfChannelWidth) + distanceArrowHorizontalOffsetFromSlab
    const yHorizontalOffset = (-xNegEdgeDistance - halfChannelLength) - distanceArrowHorizontalOffsetFromSlab

    // Defining the exact start and end points of the arrows.
    // Involves placement based on the channel and slab dimensions

    // X Positive Edge
    const xPosEdgeDistanceStartPoint = [halfChannelLength, verticalOffset, -xHorizontalOffset]
    const xPosEdgeDistanceEndPoint = [halfChannelLength + xPosEdgeDistance, verticalOffset, -xHorizontalOffset]

    const xPosEdgeReferenceLineStartPoints = [
      [halfChannelLength, verticalOffset,-halfChannelWidth],
      [halfChannelLength, verticalOffset, -(xHorizontalOffset + referenceOffsetFromArrow)],
    ]

    const xPosEdgeReferenceLineEndPoints = [
      [halfChannelLength + xPosEdgeDistance, verticalOffset, -(yPosEdgeDistance + halfChannelWidth)],
      [halfChannelLength + xPosEdgeDistance, verticalOffset, -(xHorizontalOffset + referenceOffsetFromArrow)],
    ]

    // X Negative Edge
    const xNegEdgeDistanceStartPoint = [-halfChannelLength, verticalOffset, -xHorizontalOffset]
    const xNegEdgeDistanceEndPoint = [-halfChannelLength - xNegEdgeDistance, verticalOffset, -xHorizontalOffset]

    const xNegEdgeReferenceLineStartPoints = [
      [-halfChannelLength, verticalOffset, -halfChannelWidth],
      [-halfChannelLength, verticalOffset, -(xHorizontalOffset + referenceOffsetFromArrow)],
    ]

    const xNegEdgeReferenceLineEndPoints = [
      [-halfChannelLength - xNegEdgeDistance, verticalOffset, -(yPosEdgeDistance + halfChannelWidth)],
      [-halfChannelLength - xNegEdgeDistance, verticalOffset, -(xHorizontalOffset + referenceOffsetFromArrow)],
    ]

    // Y Positive Edge
    const yPosEdgeDistanceStartPoint = [yHorizontalOffset, verticalOffset, 0]
    const yPosEdgeDistanceEndPoint = [yHorizontalOffset, verticalOffset, -halfChannelWidth - yPosEdgeDistance]

    const yPosEdgeReferenceLineStartPoints = [
      [-halfChannelLength, verticalOffset, 0],
      [yHorizontalOffset - referenceOffsetFromArrow, verticalOffset, 0],
    ]

    const yPosEdgeReferenceLineEndPoints = [
      [-(halfChannelLength + xNegEdgeDistance), verticalOffset, -(yPosEdgeDistance + halfChannelWidth)],
      [yHorizontalOffset - referenceOffsetFromArrow, verticalOffset, -(yPosEdgeDistance + halfChannelWidth)],
    ]

    // Y Negative Edge
    const yNegEdgeDistanceStartPoint = [yHorizontalOffset, verticalOffset, 0]
    const yNegEdgeDistanceEndPoint = [yHorizontalOffset, verticalOffset, halfChannelWidth + yNegEdgeDistance]

    const yNegEdgeReferenceLineStartPoints = [
      [-halfChannelLength, verticalOffset, 0],
      [yHorizontalOffset - referenceOffsetFromArrow, verticalOffset, 0],
    ]

    const yNegEdgeReferenceLineEndPoints = [
      [-(halfChannelLength + xNegEdgeDistance), verticalOffset, yNegEdgeDistance + halfChannelWidth],
      [yHorizontalOffset - referenceOffsetFromArrow, verticalOffset, yNegEdgeDistance + halfChannelWidth],
    ]

    // Push them all into an array and return
    positions.push({
      label: `${isEdgeDistanceXPosInfinite ? '∞' : Math.abs(xPosEdgeDistance)} mm`,
      distanceArrowStartPoint: xPosEdgeDistanceStartPoint as [number, number, number],
      distanceArrowEndPoint: xPosEdgeDistanceEndPoint as [number, number, number],
      startReferenceLinePoints: xPosEdgeReferenceLineStartPoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: xPosEdgeReferenceLineEndPoints as [[number, number, number], [number, number, number]],
    })

    positions.push({
      label: `${isEdgeDistanceXNegInfinite ? '∞' : Math.abs(xNegEdgeDistance)} mm`,
      distanceArrowStartPoint: xNegEdgeDistanceStartPoint as [number, number, number],
      distanceArrowEndPoint: xNegEdgeDistanceEndPoint as [number, number, number],
      startReferenceLinePoints: xNegEdgeReferenceLineStartPoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: xNegEdgeReferenceLineEndPoints as [[number, number, number], [number, number, number]],
    })

    positions.push({
      label: `${isEdgeDistanceYPosInfinite ? '∞' : Math.abs(yPosEdgeDistance)} mm`,
      distanceArrowStartPoint: yPosEdgeDistanceStartPoint as [number, number, number],
      distanceArrowEndPoint: yPosEdgeDistanceEndPoint as [number, number, number],
      startReferenceLinePoints: yPosEdgeReferenceLineStartPoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: yPosEdgeReferenceLineEndPoints as [[number, number, number], [number, number, number]],
    })

    positions.push({
      label: `${isEdgeDistanceYNegInfinite ? '∞' : Math.abs(yNegEdgeDistance)} mm`,
      distanceArrowStartPoint: yNegEdgeDistanceStartPoint as [number, number, number],
      distanceArrowEndPoint: yNegEdgeDistanceEndPoint as [number, number, number],
      startReferenceLinePoints: yNegEdgeReferenceLineStartPoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: yNegEdgeReferenceLineEndPoints as [[number, number, number], [number, number, number]],
    })

    positions.push({
      label: `${channelLength.toString()}mm`,
      distanceArrowStartPoint: [-halfChannelLength, verticalOffset, -xHorizontalOffset],
      distanceArrowEndPoint: [halfChannelLength, verticalOffset, -xHorizontalOffset],
      startReferenceLinePoints: [[0, verticalOffset, -xHorizontalOffset], [0, verticalOffset, -xHorizontalOffset]],
      endReferenceLinePoints: [[0, verticalOffset, -xHorizontalOffset], [0, verticalOffset, -xHorizontalOffset]],
    })

    return positions
  }, [slab, productVariant, component])

  return (
    <group
      position={channelOffset}
      onClick={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        dispatch(setSelectedComponent(component.id))
        dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
      }}
    >
      {
        arrowPlacements.map((item, index) => (
          <DistanceArrow3D
            key={index}
            points={[item.distanceArrowStartPoint, item.distanceArrowEndPoint]}
            referenceLinePoints={[item.startReferenceLinePoints, item.endReferenceLinePoints]}
            color="black"
            label={item.label}
          />
        ))
      }

      {
        component.model
          && <CastInChannelGLTF component={component} />
      }

      {
        !component.model
          && (
            <Html position={[loadingSpinnerHorizontalOffset, loadingSpinnerVerticalOffset, 0]}>
              <LoadingSpinner className="w-8 h-8" />
            </Html>
          )
      }
    </group>
  )
}

export default CastInChannelContainer
